/**
 * SendLogPage (iter134 — Phase 3)
 *
 * Master-admin table of every gift-card that's been issued or delivered.
 * The definitive audit trail — never returns plaintext codes.
 * Includes CSV export for reconciliation (bought / issued / unused +
 * per-customer detail).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Download, RefreshCw, Loader2, AlertCircle, CheckCircle2, Clock, Eye, Undo2, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function StatusPill({ status }) {
  const cls = {
    sent: "bg-emerald-100 text-emerald-800",
    delivered: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-800",
    pending: "bg-amber-100 text-amber-800",
    skipped: "bg-slate-100 text-slate-500",
    suppressed: "bg-slate-100 text-slate-500",
  }[status] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>
      {(status === "sent" || status === "delivered") && <CheckCircle2 size={11} />}
      {status === "failed" && <AlertCircle size={11} />}
      {status === "pending" && <Clock size={11} />}
      {status || "—"}
    </span>
  );
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, {
      year: "2-digit", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

export default function SendLogPage() {
  const { apiFetch } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [businessId, setBusinessId] = useState("");
  // iter137 — stranded panel state.
  const [stranded, setStranded] = useState([]);
  const [strandedLoading, setStrandedLoading] = useState(false);
  const [strandedError, setStrandedError] = useState(null);
  const [actionBusy, setActionBusy] = useState(null); // { gcId, action }
  const [revealed, setRevealed] = useState(null); // { gc_id, code, denomination_cents }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path = businessId
        ? `/admin/send-log?business_id=${businessId}&limit=500`
        : `/admin/send-log?limit=500`;
      const res = await apiFetch(path);
      setRows(res?.data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load send log");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, businessId]);

  const loadStranded = useCallback(async () => {
    setStrandedLoading(true);
    setStrandedError(null);
    try {
      const path = businessId
        ? `/admin/gift-cards/stranded?business_id=${businessId}`
        : `/admin/gift-cards/stranded`;
      const res = await apiFetch(path);
      setStranded(res?.data?.rows || []);
    } catch (e) {
      setStrandedError(e?.response?.data?.detail || "Failed to load stranded codes");
    } finally {
      setStrandedLoading(false);
    }
  }, [apiFetch, businessId]);

  useEffect(() => { load(); loadStranded(); }, [load, loadStranded]);

  // Fetch dealer list for the filter
  useEffect(() => {
    apiFetch("/admin/dealers").then((r) => {
      const arr = Array.isArray(r?.data) ? r.data : (r?.data?.dealers || []);
      setDealers(arr);
    }).catch(() => {});
  }, [apiFetch]);

  const downloadCsv = () => {
    const token = localStorage.getItem("token");
    const url = businessId
      ? `${API}/admin/send-log/export.csv?business_id=${businessId}`
      : `${API}/admin/send-log/export.csv`;
    // Use fetch to add auth header, then trigger a download.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `gift-card-reconciliation-${businessId || "all"}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => alert("CSV export failed"));
  };

  // iter137 — stranded actions (retry / reveal / release).
  const askReasonAndAct = async (gcId, action) => {
    const prompt = {
      retry: "Reason for the retry send (required, audit-logged):",
      reveal: "Reason for revealing this code (required, audit-logged — will be visible to any auditor):",
      release: "Reason for releasing this code back to the pool (required, audit-logged):",
    }[action];
    const reason = window.prompt(prompt);
    if (!reason || reason.trim().length < 3) return;
    setActionBusy({ gcId, action });
    try {
      const endpoint = {
        retry: `/admin/gift-cards/${gcId}/retry-delivery`,
        reveal: `/admin/gift-cards/${gcId}/reveal`,
        release: `/admin/gift-cards/${gcId}/release`,
      }[action];
      const res = await apiFetch(endpoint, { method: "POST", data: { reason } });
      if (action === "reveal") {
        setRevealed({ gc_id: gcId, ...res.data });
      }
      await loadStranded();
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="p-6" data-testid="admin-send-log-page">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gift-card send log</h1>
          <p className="text-slate-600 text-sm mt-1">
            Every code that&apos;s been issued to a customer. This is the audit trail
            and the source of truth for reconciliation. Plaintext codes never
            appear here — only who received what, when, and per-channel delivery status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={businessId}
            onChange={(e) => setBusinessId(e.target.value)}
            className="border border-slate-300 rounded-md text-sm px-3 py-2 bg-white"
            data-testid="admin-send-log-dealer-filter"
          >
            <option value="">All dealers</option>
            {dealers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <Button variant="outline" onClick={() => { load(); loadStranded(); }} disabled={loading} data-testid="admin-send-log-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </Button>
          <Button onClick={downloadCsv} data-testid="admin-send-log-download">
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* iter137 — Stranded Codes panel (super-admin only). */}
      <Card className="mb-6 border-amber-300" data-testid="admin-stranded-panel">
        <CardHeader className="bg-amber-50/50">
          <CardTitle className="text-base flex items-center gap-2 text-amber-900">
            <AlertCircle size={16} /> Stranded codes
            <span className="ml-1 text-[11px] font-normal text-amber-700">
              (issued but both channels failed — recoverable)
            </span>
            {stranded.length > 0 && (
              <span className="ml-auto text-[12px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-semibold">
                {stranded.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {strandedError && (
            <div className="p-4 text-red-800 bg-red-50 border-t border-red-200 text-sm">{strandedError}</div>
          )}
          {revealed && (
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="text-[12px] font-semibold text-slate-700 mb-1">
                Revealed code (audit-logged, do not screenshot):
              </div>
              <code className="block bg-white border border-slate-300 rounded px-3 py-2 font-mono text-lg" data-testid="admin-revealed-code">
                {revealed.code}
              </code>
              <div className="text-[11px] text-slate-500 mt-2">
                ${Math.round((revealed.denomination_cents || 0) / 100)} · Delivered manually to {revealed.customer_name || "the customer"}.
                <button
                  className="ml-2 text-blue-600 underline"
                  onClick={() => setRevealed(null)}
                  data-testid="admin-revealed-code-close"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {stranded.length === 0 && !strandedLoading && (
            <div className="p-6 text-center text-slate-400 text-sm">
              No stranded codes. All issued codes have been delivered by at least one channel.
            </div>
          )}
          {stranded.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="admin-stranded-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-semibold">Dealer</th>
                    <th className="px-4 py-2 font-semibold">Customer</th>
                    <th className="px-4 py-2 font-semibold">Phone</th>
                    <th className="px-4 py-2 font-semibold">Email</th>
                    <th className="px-4 py-2 font-semibold">Amount</th>
                    <th className="px-4 py-2 font-semibold">Issued</th>
                    <th className="px-4 py-2 font-semibold">Why stranded</th>
                    <th className="px-4 py-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stranded.map((r) => {
                    const busyKey = actionBusy?.gcId === r.gift_card_id ? actionBusy.action : null;
                    return (
                      <tr key={r.gift_card_id} className="hover:bg-amber-50/40"
                          data-testid={`stranded-row-${r.gift_card_id}`}>
                        <td className="px-4 py-2 text-slate-800">{r.dealer_name || "—"}</td>
                        <td className="px-4 py-2 font-medium text-slate-900">{r.customer_name || "—"}</td>
                        <td className="px-4 py-2 font-mono text-slate-600">{r.customer_phone_masked || "—"}</td>
                        <td className="px-4 py-2 text-slate-600">{r.customer_email_masked || "—"}</td>
                        <td className="px-4 py-2 text-slate-800">${Math.round((r.denomination_cents || 0) / 100)}</td>
                        <td className="px-4 py-2 text-slate-600">{fmt(r.issued_at)}</td>
                        <td className="px-4 py-2 text-slate-600">
                          <div className="flex flex-col gap-0.5">
                            {r.sms_status === "failed" && <span className="text-red-700">SMS: {r.sms_error || "failed"}</span>}
                            {r.email_status === "failed" && <span className="text-red-700">Email: {r.email_error || "failed"}</span>}
                            {r.email_status !== "failed" && r.email_status !== "sent" && (
                              <span className="text-slate-500">Email: {r.email_status || "not attempted"}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline"
                                    onClick={() => askReasonAndAct(r.gift_card_id, "retry")}
                                    disabled={!!actionBusy}
                                    data-testid={`stranded-retry-${r.gift_card_id}`}>
                              {busyKey === "retry" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                              <span className="ml-1">Retry</span>
                            </Button>
                            <Button size="sm" variant="outline"
                                    onClick={() => askReasonAndAct(r.gift_card_id, "reveal")}
                                    disabled={!!actionBusy}
                                    data-testid={`stranded-reveal-${r.gift_card_id}`}>
                              {busyKey === "reveal" ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                              <span className="ml-1">Reveal</span>
                            </Button>
                            <Button size="sm" variant="outline"
                                    onClick={() => askReasonAndAct(r.gift_card_id, "release")}
                                    disabled={!!actionBusy}
                                    data-testid={`stranded-release-${r.gift_card_id}`}>
                              {busyKey === "release" ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                              <span className="ml-1">Release</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {rows.length} record{rows.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {error && (
            <div className="p-4 text-red-800 bg-red-50 border-t border-red-200">{error}</div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="admin-send-log-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold">Dealer</th>
                  <th className="px-4 py-2 font-semibold">Customer</th>
                  <th className="px-4 py-2 font-semibold">Phone</th>
                  <th className="px-4 py-2 font-semibold">Amount</th>
                  <th className="px-4 py-2 font-semibold">Issued</th>
                  <th className="px-4 py-2 font-semibold">Delivered</th>
                  <th className="px-4 py-2 font-semibold">SMS</th>
                  <th className="px-4 py-2 font-semibold">Email</th>
                  <th className="px-4 py-2 font-semibold">Overall</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                      No gift cards have been issued yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.gc_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-800">{r.dealer_name || "—"}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{r.customer_name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-slate-600">{r.customer_phone || "—"}</td>
                    <td className="px-4 py-2 text-slate-800">
                      ${Math.round((r.denomination_cents || 0) / 100)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{fmt(r.issued_at)}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(r.delivered_at)}</td>
                    <td className="px-4 py-2">
                      <StatusPill status={r.sms_status || "—"} />
                      {r.sms_error && (
                        <div className="text-[10px] text-red-600 truncate max-w-[240px]" title={r.sms_error}>
                          {r.sms_error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill status={r.email_status || "—"} />
                      {r.email_error && (
                        <div className="text-[10px] text-red-600 truncate max-w-[240px]" title={r.email_error}>
                          {r.email_error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill status={r.delivery_status || "pending"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
